import type {FormEvent} from "react";
import {useState} from "react";
import type {SelectOption} from "@/components";
import {BaseAlert, BaseButton, BaseInput, BaseSelect} from "@/components";
import type {RegistrationFormValues} from "./registration.schema";
import {registrationSchema} from "./registration.schema";
import type {PreferredBonus} from "./registration.types";
import {useRegistration} from "./useRegistration";

export interface RegistrationPageProps {
  onSuccess?: () => void;
}

type FieldErrors = Partial<Record<keyof RegistrationFormValues, string>>;

const bonusOptions: SelectOption<PreferredBonus>[] = [
  {value: "slot", label: "Slots"},
  {value: "sport", label: "Deportes"},
  {value: "casino", label: "Casino"},
];

const initialValues: RegistrationFormValues = {
  email: "",
  password: "",
  confirmPassword: "",
  birthDate: "",
  preferredBonus: "slot",
  agreeToTerms: false,
};

const RegistrationPage = ({onSuccess}: RegistrationPageProps) => {
  const {register, isPending, isError, error} = useRegistration();
  const [values, setValues] = useState<RegistrationFormValues>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});

  const updateField = <K extends keyof RegistrationFormValues>(field: K, value: RegistrationFormValues[K]) => {
    setValues(prev => ({...prev, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = registrationSchema.safeParse(values);
    if (!result.success) {
      const next: FieldErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !next[key as keyof RegistrationFormValues]) {
          next[key as keyof RegistrationFormValues] = issue.message;
        }
      }
      setErrors(next);
      return;
    }
    register(
      {
        email: result.data.email,
        password: result.data.password,
        birthDate: result.data.birthDate,
        preferredBonus: result.data.preferredBonus,
      },
      {onSuccess: () => onSuccess?.()}
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-[460px] flex-col gap-6 rounded-[var(--radius-xl)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-lg)]">
      <header className="flex flex-col gap-2 text-center">
        <h1 className="m-0 text-[var(--font-h1)] font-bold text-[var(--color-text)]">Crear cuenta</h1>
        <p className="m-0 text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Debes ser mayor de 18 años para registrarte
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
        noValidate
      >
        <BaseInput
          type="email"
          label="Correo electrónico"
          placeholder="tu@correo.com"
          autoComplete="email"
          value={values.email}
          onChange={event => updateField("email", event.target.value)}
          error={errors.email}
          fullWidth
          required
        />

        <BaseInput
          type="date"
          label="Fecha de nacimiento"
          value={values.birthDate}
          onChange={event => updateField("birthDate", event.target.value)}
          error={errors.birthDate}
          fullWidth
          required
        />

        <BaseInput
          type="password"
          label="Contraseña"
          placeholder="Mínimo 8 caracteres"
          autoComplete="new-password"
          hint="Al menos 8 caracteres, 1 mayúscula y 1 número"
          value={values.password}
          onChange={event => updateField("password", event.target.value)}
          error={errors.password}
          fullWidth
          required
        />

        <BaseInput
          type="password"
          label="Confirmar contraseña"
          autoComplete="new-password"
          value={values.confirmPassword}
          onChange={event => updateField("confirmPassword", event.target.value)}
          error={errors.confirmPassword}
          fullWidth
          required
        />

        <BaseSelect
          label="Bono preferido"
          options={bonusOptions}
          value={values.preferredBonus}
          onChange={event => updateField("preferredBonus", event.target.value as PreferredBonus)}
          error={errors.preferredBonus}
          fullWidth
        />

        <label className="flex items-start gap-2 text-[var(--font-body)] text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            className="mt-[3px] h-4 w-4 accent-[var(--color-primary)]"
            checked={values.agreeToTerms}
            onChange={event => updateField("agreeToTerms", event.target.checked)}
          />
          <span>
            Acepto los{" "}
            <a
              href="/terminos-y-condiciones"
              className="text-[var(--color-primary)] underline"
            >
              términos y condiciones
            </a>
          </span>
        </label>
        {errors.agreeToTerms && (
          <p className="m-0 -mt-2 text-[var(--font-body)] text-[var(--color-danger)]">{errors.agreeToTerms}</p>
        )}

        {isError && <BaseAlert variant="danger">{error?.message ?? "No se pudo completar el registro"}</BaseAlert>}

        <BaseButton
          type="submit"
          variant="primary"
          size="lg"
          loading={isPending}
          fullWidth
        >
          Crear cuenta
        </BaseButton>
      </form>
    </div>
  );
};

export default RegistrationPage;
